<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Draggable from 'vuedraggable';
import { useAlert } from 'dashboard/composables';
import { useCrmStore } from '../store';
import { useMapGetter } from 'dashboard/composables/store';
import { closeReasonsAPI } from '../api';

import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import ColorPicker from 'dashboard/components-next/colorpicker/ColorPicker.vue';
import ConfirmDialog from 'dashboard/wasapi/common/ConfirmDialog.vue';
import DialogSteps from 'dashboard/wasapi/common/DialogSteps.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import StageAutomationsDialog from './StageAutomationsDialog.vue';

const emit = defineEmits(['saved']);

const { t } = useI18n();
const store = useCrmStore();
const agents = useMapGetter('agents/getVerifiedAgents');

const dialogRef = ref(null);
const pipelineId = ref(null);
const name = ref('');
const stages = ref([]);
const removedIds = ref([]);
const customFields = ref([]);
const automationsDialogRef = ref(null);
const requireWonReason = ref(false);
const requireLostReason = ref(false);
const wonReasons = ref([]);
const lostReasons = ref([]);
const newReason = ref({ won: '', lost: '' });
const isPublic = ref(true);
const allowedUserIds = ref([]);

const FIELD_TYPES = ['text', 'number', 'date', 'select', 'checkbox'];
const fieldTypeOptions = computed(() =>
  FIELD_TYPES.map(type => ({
    value: type,
    label: t(`WASAPI_CRM.FIELDS.TYPES.${type.toUpperCase()}`),
  }))
);
const addField = () =>
  customFields.value.push({ key: '', label: '', type: 'text', options: '' });
const removeField = index => customFields.value.splice(index, 1);
const slugify = value =>
  String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 40);

const toggleAllowedUser = id => {
  const i = allowedUserIds.value.indexOf(id);
  if (i >= 0) allowedUserIds.value.splice(i, 1);
  else allowedUserIds.value.push(id);
};
const openAutomations = stage => automationsDialogRef.value?.open(stage);
const onAutomationsSaved = ({ stageId, inboxId, automations }) => {
  const stage = stages.value.find(
    s => s.id === stageId || s.localKey === stageId
  );
  if (!stage) return;
  stage.inboxId = inboxId;
  stage.automations = automations;
};

const isSaving = computed(() => store.uiFlags.savingPipeline);
// custom fields need a real, unique key (an emoji-only label slugifies to '')
const fieldKeys = computed(() =>
  customFields.value.map(field => field.key || slugify(field.label))
);
const invalidFieldIndexes = computed(() => {
  const seen = new Set();
  return fieldKeys.value.reduce((bad, key, index) => {
    if (!key || seen.has(key)) bad.add(index);
    seen.add(key);
    return bad;
  }, new Set());
});
const isInvalid = computed(
  () =>
    !name.value.trim() ||
    stages.value.length === 0 ||
    stages.value.some(s => !s.name.trim()) ||
    invalidFieldIndexes.value.size > 0
);

const categoryOptions = computed(() => [
  { value: 'open', label: t('WASAPI_CRM.STAGES.CATEGORY.OPEN') },
  { value: 'won', label: t('WASAPI_CRM.STAGES.CATEGORY.WON') },
  { value: 'lost', label: t('WASAPI_CRM.STAGES.CATEGORY.LOST') },
]);

const step = ref(1);
const stepOptions = computed(() => [
  { value: 1, label: t('WASAPI_CRM.STAGES.STEP_PIPELINE') },
  { value: 2, label: t('WASAPI_CRM.STAGES.STEP_FIELDS') },
]);

const open = (pipeline = null) => {
  step.value = 1;
  pipelineId.value = pipeline?.id || null;
  name.value = pipeline?.name || '';
  stages.value = (pipeline?.stages || []).map(s => ({ ...s, localKey: s.id }));
  customFields.value = (pipeline?.customFields || []).map(f => ({
    ...f,
    options: (f.options || []).join(', '),
  }));
  removedIds.value = [];
  isPublic.value = pipeline ? pipeline.isPublic !== false : true;
  allowedUserIds.value = [...(pipeline?.allowedUserIds || [])];
  requireWonReason.value = !!pipeline?.requireWonReason;
  requireLostReason.value = !!pipeline?.requireLostReason;
  wonReasons.value = (pipeline?.closeReasons || []).filter(
    r => r.kind === 'won'
  );
  lostReasons.value = (pipeline?.closeReasons || []).filter(
    r => r.kind === 'lost'
  );
  newReason.value = { won: '', lost: '' };
  dialogRef.value?.open();
};

const addReason = async kind => {
  const label = newReason.value[kind].trim();
  if (!label || !pipelineId.value) return;
  const { data } = await closeReasonsAPI(pipelineId.value).create({
    kind,
    name: label,
  });
  (kind === 'won' ? wonReasons : lostReasons).value.push(data);
  newReason.value[kind] = '';
};
const removeReason = async (kind, reason) => {
  if (reason.id && pipelineId.value)
    await closeReasonsAPI(pipelineId.value).delete(reason.id);
  const list = kind === 'won' ? wonReasons : lostReasons;
  list.value = list.value.filter(r => r.id !== reason.id);
};

const addStage = () => {
  stages.value.push({
    localKey: `new-${Date.now()}`,
    id: null,
    name: '',
    color: '#3B82F6',
    category: 'open',
  });
};

const confirmRef = ref(null);
const dropStage = index => {
  const stage = stages.value[index];
  stages.value.splice(index, 1);
  if (stage.id) removedIds.value.push(stage.id);
};
const removeStage = index => {
  const stage = stages.value[index];
  if (!stage.id) {
    dropStage(index);
    return;
  }
  confirmRef.value?.open({
    title: t('WASAPI_CRM.STAGES.REMOVE_CONFIRM', { name: stage.name }),
    confirmLabel: t('WASAPI_COMMON.REMOVE'),
    onConfirm: () => dropStage(index),
  });
};

const handleSave = async () => {
  if (isInvalid.value) return;
  const stagesAttributes = [
    ...stages.value.map((s, position) => ({
      id: s.id || undefined,
      name: s.name.trim(),
      color: s.color,
      category: s.category,
      position,
      inboxId: s.inboxId || null,
      automations: s.automations || [],
    })),
    ...removedIds.value.map(id => ({ id, _destroy: true })),
  ];
  try {
    const pipeline = await store.savePipeline(pipelineId.value, {
      name: name.value.trim(),
      requireWonReason: requireWonReason.value,
      requireLostReason: requireLostReason.value,
      isPublic: isPublic.value,
      allowedUserIds: isPublic.value ? [] : allowedUserIds.value,
      stagesAttributes,
      customFields: customFields.value
        .filter(f => f.label.trim())
        .map(f => ({
          key: f.key || slugify(f.label),
          label: f.label.trim(),
          type: f.type,
          options:
            f.type === 'select'
              ? String(f.options || '')
                  .split(',')
                  .map(o => o.trim())
                  .filter(Boolean)
              : [],
        })),
    });
    useAlert(t('WASAPI_CRM.STAGES.SAVED'));
    emit('saved', pipeline);
    dialogRef.value?.close();
  } catch (error) {
    const message =
      error?.response?.data?.message || error?.response?.data?.error;
    useAlert(message || t('WASAPI_CRM.STAGES.SAVE_ERROR'));
  }
};

defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="2xl"
    overflow-y-auto
    :title="
      t(
        pipelineId
          ? 'WASAPI_CRM.STAGES.EDIT_TITLE'
          : 'WASAPI_CRM.STAGES.NEW_TITLE'
      )
    "
    :description="t('WASAPI_CRM.STAGES.DESCRIPTION')"
    :show-cancel-button="false"
    :show-confirm-button="false"
  >
    <DialogSteps v-model="step" :steps="stepOptions" />
    <div v-show="step === 1" class="flex flex-col gap-4">
      <Input
        v-model="name"
        :label="t('WASAPI_CRM.STAGES.PIPELINE_NAME')"
        :disabled="isSaving"
      />

      <div class="flex flex-col gap-2">
        <label class="inline-flex items-center gap-1.5 text-sm text-n-slate-12">
          <input v-model="isPublic" type="checkbox" class="!mb-0" />
          {{ t('WASAPI_CRM.VISIBILITY.PUBLIC') }}
        </label>
        <div v-if="!isPublic" class="flex flex-col gap-1">
          <span class="text-xs text-n-slate-11">{{
            t('WASAPI_CRM.VISIBILITY.HINT')
          }}</span>
          <div class="flex flex-wrap gap-1.5">
            <button
              v-for="agent in agents || []"
              :key="agent.id"
              type="button"
              class="px-2.5 py-1 text-xs rounded-full border"
              :class="
                allowedUserIds.includes(agent.id)
                  ? 'bg-n-blue-9 text-white border-n-blue-9'
                  : 'border-n-weak text-n-slate-11 hover:bg-n-alpha-1'
              "
              @click="toggleAllowedUser(agent.id)"
            >
              {{ agent.name }}
            </button>
          </div>
        </div>
      </div>
      <div class="flex flex-col gap-2">
        <span class="text-sm font-medium text-n-slate-12">
          {{ t('WASAPI_CRM.STAGES.TITLE') }}
        </span>
        <Draggable
          v-model="stages"
          item-key="localKey"
          handle=".stage-handle"
          animation="150"
          class="flex flex-col gap-2"
        >
          <template #item="{ element, index }">
            <div class="flex items-center gap-2">
              <Icon
                icon="i-lucide-grip-vertical"
                class="stage-handle size-4 text-n-slate-10 cursor-grab shrink-0"
              />
              <ColorPicker v-model="element.color" class="shrink-0" />
              <Input
                v-model="element.name"
                :placeholder="t('WASAPI_CRM.STAGES.NAME_PLACEHOLDER')"
                class="flex-1"
                custom-input-class="h-8 !py-1"
                :autofocus="!element.id"
              />
              <div class="w-36 shrink-0">
                <ComboBox
                  v-model="element.category"
                  :options="categoryOptions"
                />
              </div>
              <Button
                v-tooltip="t('WASAPI_CRM.AUTOMATIONS.OPEN')"
                icon="i-lucide-zap"
                variant="ghost"
                :color="
                  element.automations?.length || element.inboxId
                    ? 'blue'
                    : 'slate'
                "
                size="sm"
                :disabled="!element.id"
                @click="openAutomations(element)"
              />
              <Button
                icon="i-lucide-trash-2"
                variant="ghost"
                color="ruby"
                size="sm"
                @click="removeStage(index)"
              />
            </div>
          </template>
        </Draggable>
        <Button
          :label="t('WASAPI_CRM.STAGES.ADD')"
          icon="i-lucide-plus"
          variant="faded"
          color="slate"
          size="sm"
          class="self-start"
          @click="addStage"
        />
      </div>
    </div>

    <div v-show="step === 2" class="flex flex-col gap-4">
      <div class="flex flex-col gap-2">
        <span class="text-sm font-medium text-n-slate-12">
          {{ t('WASAPI_CRM.FIELDS.TITLE') }}
        </span>
        <div
          v-for="(field, index) in customFields"
          :key="index"
          class="grid grid-cols-[1fr_1fr_auto_auto] gap-2 items-center"
        >
          <Input
            v-model="field.label"
            :placeholder="t('WASAPI_CRM.FIELDS.LABEL')"
            custom-input-class="h-8 !py-1"
            :message="
              invalidFieldIndexes.has(index)
                ? t('WASAPI_CRM.FIELDS.KEY_INVALID')
                : ''
            "
            :message-type="invalidFieldIndexes.has(index) ? 'error' : 'info'"
            @input="!field.id && (field.key = slugify(field.label))"
          />
          <Input
            v-if="field.type === 'select'"
            v-model="field.options"
            :placeholder="t('WASAPI_CRM.FIELDS.OPTIONS')"
            custom-input-class="h-8 !py-1"
          />
          <span v-else class="text-xs text-n-slate-10 truncate">{{
            field.key || slugify(field.label)
          }}</span>
          <div class="w-40">
            <ComboBox v-model="field.type" :options="fieldTypeOptions" />
          </div>
          <Button
            icon="i-lucide-trash-2"
            variant="ghost"
            color="ruby"
            size="sm"
            @click="removeField(index)"
          />
        </div>
        <Button
          :label="t('WASAPI_CRM.FIELDS.ADD')"
          icon="i-lucide-plus"
          variant="faded"
          color="slate"
          size="sm"
          class="self-start"
          @click="addField"
        />
      </div>
      <div class="flex flex-col gap-3 pt-3 mt-2 border-t border-n-weak">
        <span class="text-sm font-medium text-n-slate-12">
          {{ t('WASAPI_CRM.CLOSE_REASON.MANAGE') }}
        </span>
        <p class="text-xs text-n-slate-11">
          {{ t('WASAPI_CRM.CLOSE_REASON.MANAGE_HINT') }}
        </p>
        <div v-if="!pipelineId" class="text-xs text-n-slate-10">
          {{ t('WASAPI_CRM.CLOSE_REASON.SAVE_FIRST') }}
        </div>
        <div v-else class="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div
            v-for="kind in ['won', 'lost']"
            :key="kind"
            class="flex flex-col gap-2"
          >
            <label
              class="inline-flex items-center gap-1.5 text-sm text-n-slate-12"
            >
              <input
                type="checkbox"
                class="!mb-0"
                :checked="kind === 'won' ? requireWonReason : requireLostReason"
                @change="
                  kind === 'won'
                    ? (requireWonReason = $event.target.checked)
                    : (requireLostReason = $event.target.checked)
                "
              />
              {{
                kind === 'won'
                  ? t('WASAPI_CRM.CLOSE_REASON.REQUIRE_WON')
                  : t('WASAPI_CRM.CLOSE_REASON.REQUIRE_LOST')
              }}
            </label>
            <div
              v-for="reason in kind === 'won' ? wonReasons : lostReasons"
              :key="reason.id"
              class="flex items-center gap-2 px-2 py-1 text-sm rounded bg-n-alpha-1"
            >
              <span class="flex-1 truncate text-n-slate-12">{{
                reason.name
              }}</span>
              <Button
                icon="i-lucide-trash-2"
                variant="ghost"
                color="ruby"
                size="xs"
                type="button"
                @click="removeReason(kind, reason)"
              />
            </div>
            <div class="flex items-center gap-2">
              <Input
                v-model="newReason[kind]"
                class="flex-1"
                size="sm"
                :placeholder="t('WASAPI_CRM.CLOSE_REASON.NEW')"
                @keydown.enter.prevent.stop="addReason(kind)"
              />
              <Button
                :label="t('WASAPI_CRM.CLOSE_REASON.ADD')"
                variant="faded"
                color="slate"
                size="sm"
                type="button"
                @click="addReason(kind)"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <template #footer>
      <div class="flex items-center justify-between w-full gap-3">
        <Button
          v-if="step === 2"
          :label="t('WASAPI_COMMON.BACK')"
          icon="i-lucide-arrow-left"
          variant="ghost"
          color="slate"
          @click="step = 1"
        />
        <span v-else />
        <div class="flex items-center gap-3">
          <Button
            :label="t('DIALOG.BUTTONS.CANCEL')"
            variant="faded"
            color="slate"
            @click="dialogRef.close()"
          />
          <Button
            v-if="step === 1"
            :label="t('WASAPI_COMMON.NEXT')"
            variant="faded"
            color="slate"
            @click="step = 2"
          />
          <Button
            :label="t('WASAPI_CRM.STAGES.SAVE')"
            color="blue"
            :disabled="isInvalid || isSaving"
            :is-loading="isSaving"
            @click="handleSave"
          />
        </div>
      </div>
    </template>
    <StageAutomationsDialog
      ref="automationsDialogRef"
      @save="onAutomationsSaved"
    />
    <ConfirmDialog ref="confirmRef" />
  </Dialog>
</template>
